import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Car, Clock, CheckCircle, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

export default function TodayView() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [enCours, setEnCours] = useState(0);
  const [termines, setTermines] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchToday();
  }, []);

  const fetchToday = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planning/today");
      setVehicles(response.data.vehicles || []);
      setTotal(response.data.total || 0);
      setEnCours(response.data.en_cours || 0);
      setTermines(response.data.termines || 0);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const getStepColor = (step) => {
    const colors = {
      demontage: "bg-amber-100 text-amber-700",
      redressage: "bg-orange-100 text-orange-700",
      peinture: "bg-blue-100 text-blue-700",
      remontage: "bg-purple-100 text-purple-700",
      livraison: "bg-emerald-100 text-emerald-700",
      termine: "bg-emerald-100 text-emerald-700",
      none: "bg-slate-100 text-slate-600",
    };
    return colors[step] || colors.none;
  };

  const getStatusColor = (status) => {
    const colors = {
      en_attente: "bg-amber-100 text-amber-700",
      en_cours: "bg-blue-100 text-blue-700",
      termine: "bg-emerald-100 text-emerald-700",
    };
    return colors[status] || "";
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="today-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Aujourd'hui</h1>
          <p className="text-slate-600 mt-1">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        {user?.role === "admin" && (
          <Button onClick={() => navigate("/vehicles/new")} className="bg-red-600 hover:bg-red-700 text-white">
            Nouveau RDV
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <Car className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{total}</p>
              <p className="text-sm text-slate-600">RDV du jour</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{enCours}</p>
              <p className="text-sm text-slate-600">En cours</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{termines}</p>
              <p className="text-sm text-slate-600">Terminés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Véhicules du jour
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vehicles.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Car className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold">Aucun RDV aujourd'hui</p>
            </div>
          ) : (
            <div className="space-y-3">
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-white hover:border-red-300 cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${getStepColor(vehicle.workflow_step)}`}>
                      <Car className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-lg font-bold text-slate-900">
                          {vehicle.immatriculation}
                        </p>
                        <Badge className={getStatusColor(vehicle.statut)}>
                          {vehicle.statut === "en_attente" ? "En attente" : vehicle.statut === "en_cours" ? "En cours" : "Terminé"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        {vehicle.marque} {vehicle.modele} - {vehicle.client.nom}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
